import styled from "@emotion/styled";
import { SearchIcon, SparklesIcon } from "lucide-react";
import type { FC, FormEvent } from "react";
import { useScrollVisibility } from "@/hooks/use-scroll-visibility";
import { useDrawerState } from "@/store/drawer-state";
import { useChatState } from "@/store/chat-state";
import { useLimitedInput } from "@/hooks/use-limited-input";
import { useAnimatedHeight } from "@/hooks/use-animated-height";
import { WIDGET_ORIGIN } from "@/constants/widget-origin";
import type { BootstrapHandle } from "@/utils/bootstrap-web-component";
import { bootstrapWidgetWebComponent } from "@/utils/bootstrap-web-component";
import { FLOATING_SEARCH_PLACEHOLDER } from "@/constants/search-constants";
import { resolvePlaceholder } from "@/utils/resolve-widget-content";
import {
  backgrounds,
  borders,
  breakpoints,
  colors,
  fonts,
  iconGradients,
  radius,
  responsiveSpacing,
  shadows,
  spacing,
} from "../design-tokens";

// Scroll visibility constants
const SHOW_AFTER_VH = 0.3; // Show after scrolling 30% of viewport height
const HIDE_BEFORE_BOTTOM_VH = 0.2; // Hide when within 20% of viewport height from bottom

type SearchBoxProps = {
  placeholder?: string;
};

const Wrapper = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  z-index: 999;
  padding-inline: ${responsiveSpacing.safeArea.desktop}px;
  box-sizing: border-box;
  transition:
    opacity 300ms ease-in-out,
    bottom 300ms ease-in-out;

  /* Hidden state by default */
  opacity: 0;
  bottom: ${spacing.xl}px;
  pointer-events: none;

  &.visible {
    opacity: 1;
    bottom: ${responsiveSpacing.bottomOffset.desktop}px;
  }

  @media (max-width: ${breakpoints.md - 1}px) {
    padding-inline: ${responsiveSpacing.safeArea.mobile}px;

    &.visible {
      bottom: ${responsiveSpacing.bottomOffset.mobile}px;
    }
  }
`;

const StyledForm = styled.form<{ hasError?: boolean }>`
  margin-inline: auto;
  width: 100%;
  max-width: 560px;
  border-radius: ${radius.lg}px;
  ${props => (props.hasError ? `border: 1px solid ${colors.danger};` : borders.input)}
  ${backgrounds.glassy}
  ${shadows.searchBox}
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: ${spacing.sm}px ${spacing.lg}px ${spacing.sm}px ${spacing.sm}px;
  pointer-events: auto;
  box-sizing: border-box;
`;

const InputRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md}px;
  width: 100%;
`;

const IconContainer = styled.div`
  ${iconGradients.sparkle}
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
`;

const StyledSparklesIcon = styled(SparklesIcon)`
  width: 20px;
  height: 20px;
  color: #fff;
  fill: #fff;
  stroke-width: 1;
`;

const StyledInput = styled.input`
  ${fonts.bodyFont}
  border: none;
  outline: none;
  background: transparent;
  font-size: 16px;
  color: ${colors.text};
  flex-grow: 1;
  min-width: 0;
  font-weight: 400;
  padding: 0 8px;
`;

const SubmitButton = styled.button`
  background: transparent;
  border: none;
  border-radius: ${radius.sm}px;
  width: ${spacing["4xl"]}px;
  height: ${spacing["4xl"]}px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 200ms ease;

  &:hover {
    background: color-mix(in srgb, var(--pollen-primary-color, #2563eb) 10%, transparent);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const StyledSearchIcon = styled(SearchIcon)`
  width: 20px;
  height: 20px;
  color: ${colors.iconAccent};
  stroke-width: 2;
`;

const ErrorContainer = styled.div<{
  height: number | "auto";
  hasError: boolean;
}>`
  overflow: hidden;
  height: ${props =>
    typeof props.height === "number" ? `${props.height}px` : props.height};
  transition: height 200ms ease-in-out;
  margin-top: ${props => (props.hasError ? spacing.xs : 0)}px;
`;

const InlineError = styled.div`
  color: ${colors.danger};
  font-size: 12px;
  padding-left: 56px; // 40px icon + 16px gap
  padding-top: ${spacing.xs}px;
  padding-bottom: ${spacing.xs}px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const SearchBoxComponent: FC<SearchBoxProps> = ({
  placeholder = FLOATING_SEARCH_PLACEHOLDER,
}) => {
  const limitedInput = useLimitedInput("", { showError: true });
  const { isDrawerOpen, openDrawer } = useDrawerState();
  const { submitQuery } = useChatState();
  const { ref: errorRef, animatedHeight } = useAnimatedHeight(
    !!limitedInput.error,
  );

  const isSearchBoxVisible = useScrollVisibility({
    showAfterVh: SHOW_AFTER_VH,
    hideBeforeBottomVh: HIDE_BEFORE_BOTTOM_VH,
    enabled: !isDrawerOpen,
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (limitedInput.value.trim() && limitedInput.isValid) {
      submitQuery(limitedInput.value.trim(), WIDGET_ORIGIN.SEARCH_BOX);
      openDrawer();
      limitedInput.reset();
    }
  };

  return (
    <Wrapper className={isSearchBoxVisible ? "visible" : ""}>
      <StyledForm onSubmit={handleSubmit} hasError={!!limitedInput.error}>
        <InputRow>
          <IconContainer>
            <StyledSparklesIcon />
          </IconContainer>
          <StyledInput
            type="text"
            name="query"
            value={limitedInput.value}
            onChange={limitedInput.handleChange}
            placeholder={placeholder}
          />
          <SubmitButton
            type="submit"
            disabled={!limitedInput.value.trim() || !limitedInput.isValid}
          >
            <StyledSearchIcon />
          </SubmitButton>
        </InputRow>
        <ErrorContainer height={animatedHeight} hasError={!!limitedInput.error}>
          <InlineError ref={errorRef}>
            {limitedInput.error || "\u00A0"}
          </InlineError>
        </ErrorContainer>
      </StyledForm>
    </Wrapper>
  );
};

class SearchBoxWebComponent extends HTMLElement {
  private handle: BootstrapHandle | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  connectedCallback() {
    const placeholder = this.getAttribute("placeholder") ?? undefined;
    this.handle = bootstrapWidgetWebComponent(this, {
      componentName: "search-box",
      render: config => (
        <SearchBoxComponent
          placeholder={resolvePlaceholder(
            placeholder,
            config.placeholderText,
            FLOATING_SEARCH_PLACEHOLDER,
          )}
        />
      ),
    });
  }

  disconnectedCallback() {
    this.handle?.unmount();
    this.handle = null;
  }
}

export default SearchBoxWebComponent;
